import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  message,
  Spin,
  Empty,
  Popconfirm,
  Input,
  Form,
  Select,
  AutoComplete,
  Radio,
  Tag
} from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import CustomerSelector from './CustomerSelector';
import MemberSelector from './MemberSelector';

// 主题定义（与VenueDashboard.js保持一致）
const themes = {
  default: {
    name: '默认主题',
    backgroundColor: '#f5f5f5',
    cardBackground: '#ffffff',
    cardBorder: '#f0f0f0',
    textPrimary: '#333333',
    textSecondary: '#666666',
    textMuted: '#999999',
    accentColor: '#1890ff',
    onlineColor: '#52c41a',
    offlineColor: '#a6a0a0',
    heartRateLow: 'green',
    heartRateNormal: 'orange',
    heartRateHigh: 'red'
  },
  dark: {
    name: '深色主题',
    backgroundColor: '#1a1a1a',
    cardBackground: '#2a2a2a',
    cardBorder: '#404040',
    textPrimary: '#ffffff',
    textSecondary: '#cccccc',
    textMuted: '#888888',
    accentColor: '#40a9ff',
    onlineColor: '#73d13d',
    offlineColor: '#595959',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  },
  blue: {
    name: '蓝色主题',
    backgroundColor: '#e6f7ff',
    cardBackground: '#ffffff',
    cardBorder: '#bae7ff',
    textPrimary: '#003a8c',
    textSecondary: '#0050b3',
    textMuted: '#69c0ff',
    accentColor: '#1890ff',
    onlineColor: '#52c41a',
    offlineColor: '#a6a0a0',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  },
  green: {
    name: '绿色主题',
    backgroundColor: '#f6ffed',
    cardBackground: '#ffffff',
    cardBorder: '#b7eb8f',
    textPrimary: '#135200',
    textSecondary: '#237804',
    textMuted: '#52c41a',
    accentColor: '#52c41a',
    onlineColor: '#52c41a',
    offlineColor: '#a6a0a0',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  },
  purple: {
    name: '紫色主题',
    backgroundColor: '#f9f0ff',
    cardBackground: '#ffffff',
    cardBorder: '#d3adf7',
    textPrimary: '#531dab',
    textSecondary: '#722ed1',
    textMuted: '#b37feb',
    accentColor: '#722ed1',
    onlineColor: '#52c41a',
    offlineColor: '#a6a0a0',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  }
};

// 手环昵称选择器组件 - 平铺标签形式
const BandNickNameSelector = ({ options, form, fieldName }) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  
  // 两种颜色轮询的样式
  const tagStyles = [
    {
      backgroundColor: '#e6f7ff',
      borderColor: '#1890ff',
      color: '#096dd9'
    },
    {
      backgroundColor: '#f6ffed',
      borderColor: '#52c41a',
      color: '#389e0d'
    }
  ];
  
  // 同步表单值
  useEffect(() => {
    const currentValue = form.getFieldValue(fieldName);
    if (currentValue !== undefined && currentValue !== selectedValue) {
      setSelectedValue(currentValue);
      setInputValue(currentValue);
    }
  }, [form, fieldName, selectedValue]);
  
  const handleTagClick = (value) => {
    setSelectedValue(value);
    setInputValue(value);
    form.setFieldsValue({ [fieldName]: value });
  };
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedValue(value);
    form.setFieldsValue({ [fieldName]: value });
  };
  
  // 判断是否显示快速选择标签（名称为空时展示）
  const showQuickSelect = !inputValue || inputValue.trim() === '';
  
  return (
    <div>
      {/* 输入框 */}
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder="请输入或选择手环昵称"
        allowClear
        onClear={() => {
          setInputValue('');
          setSelectedValue('');
          form.setFieldsValue({ [fieldName]: '' });
        }}
        style={{ marginBottom: showQuickSelect ? 12 : 0 }}
      />
      
      {/* 平铺标签 - 仅当名称为空时展示 */}
      {showQuickSelect && options.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ 
            fontSize: '12px', 
            color: '#999', 
            marginBottom: 8 
          }}>
            快速选择：
          </div>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 8 
          }}>
            {options.map((option, index) => {
              const isSelected = selectedValue === option.value;
              const style = tagStyles[index % 2];
              return (
                <div
                  key={option.value}
                  onClick={() => handleTagClick(option.value)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    border: `1px solid ${style.borderColor}`,
                    backgroundColor: isSelected ? style.borderColor : style.backgroundColor,
                    color: isSelected ? '#fff' : style.color,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    userSelect: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = style.borderColor;
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = style.backgroundColor;
                      e.currentTarget.style.color = style.color;
                    }
                  }}
                >
                  {option.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerBandsViewer = ({ triggerButtonProps = {}, modalProps = {}, onBandsChange, autoOpen = false }) => {
  const { apiClient } = useAuth();
  const [visible, setVisible] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    // 优先使用缓存的选择
    const cached = localStorage.getItem('customerBandSelectedId');
    if (cached) {
      return cached;
    }
    
    // 否则使用用户默认校区
    try {
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      return savedUser?.customerId || undefined;
    } catch (err) {
      console.warn('解析用户信息失败:', err);
      return undefined;
    }
  });
  
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  
  // 心率告警阈值状态
  const [heartRateThreshold, setHeartRateThreshold] = useState(() => {
    try {
      const savedThreshold = localStorage.getItem('venueDashboardHeartRateThreshold');
      return savedThreshold ? parseInt(savedThreshold) : 200;
    } catch (err) {
      return 200;
    }
  });
  
  // 主题状态（用于配置区域样式）
  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      return localStorage.getItem('venueDashboardTheme') || 'default';
    } catch (err) {
      return 'default';
    }
  });
  
  // 会员绑定相关状态
  const [selectedMemberForAdd, setSelectedMemberForAdd] = useState(null);
  const [selectedMemberForEdit, setSelectedMemberForEdit] = useState(null);
  
  // 手环昵称枚举标签选项
  const [bandNickNameOptions, setBandNickNameOptions] = useState([]);
  
  // 网关信息状态
  const [gatewayInfo, setGatewayInfo] = useState(null);
  const [gatewayList, setGatewayList] = useState([]);
  const [gatewayModalVisible, setGatewayModalVisible] = useState(false);
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [gatewayAddModalVisible, setGatewayAddModalVisible] = useState(false);
  const [gatewayEditModalVisible, setGatewayEditModalVisible] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [gatewayAddForm] = Form.useForm();
  const [gatewayEditForm] = Form.useForm();

  useEffect(() => {
    if (selectedCustomerId) {
      localStorage.setItem('customerBandSelectedId', selectedCustomerId);
    }
  }, [selectedCustomerId]);
  
  // autoOpen 为 true 时自动打开弹窗
  useEffect(() => {
    if (autoOpen) {
      setVisible(true);
    }
  }, [autoOpen]);
  
  // 获取手环昵称枚举标签
  const fetchBandNickNameOptions = useCallback(async () => {
    if (!apiClient) return;
    try {
      const res = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'BAND_NICK_NAME',
        colName: 'BAND_NICK_NAME'
      });
      
      if (res.data?.code === 200) {
        let rawData = res.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        const options = (rawData || []).map(item => ({
          label: item.enumName,
          value: item.enumName
        }));
        setBandNickNameOptions(options);
      }
    } catch (error) {
      console.error('获取手环昵称枚举标签失败:', error);
    }
  }, [apiClient]);
  
  // 组件挂载时获取枚举标签
  useEffect(() => {
    fetchBandNickNameOptions();
  }, [fetchBandNickNameOptions]);
  
  // 获取网关信息
  const fetchGatewayInfo = useCallback(async () => {
    if (!selectedCustomerId || !apiClient) {
      setGatewayInfo(null);
      setGatewayList([]);
      return;
    }
    try {
      // 当前使用的旧接口 - 后续需要替换为新接口
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'CUSTOMER_GATEWAY',
        colName: selectedCustomerId
      });
      
      if (resp.data?.code === 200) {
        let rawData = resp.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        const list = Array.isArray(rawData) ? rawData : [];
        const normalized = list
          .filter(item => !item.colName || String(item.colName) === String(selectedCustomerId))
          .map(item => ({
            configId: item.configId,
            enumCode: item.enumCode,
            enumName: item.enumName || item.enumCode,
            remark: item.remark || '',
            showSort: item.showSort ?? 0,
            state: item.state ?? 1
          }))
          .sort((a, b) => (a.showSort ?? 0) - (b.showSort ?? 0));
        
        setGatewayList(normalized);
        
        if (normalized.length > 0) {
          // ENUM_CODE为网关编码，多个时候用分号分割
          const gatewayCodes = normalized.map(item => item.enumCode).filter(Boolean).join('; ');
          setGatewayInfo(gatewayCodes || '未配置');
        } else {
          setGatewayInfo('未配置');
        }
      }
    } catch (error) {
      console.error('获取网关信息失败:', error);
      setGatewayInfo('获取失败');
      setGatewayList([]);
    }
  }, [selectedCustomerId, apiClient]);
  
  // 当selectedCustomerId变化时获取网关信息
  useEffect(() => {
    if (selectedCustomerId && apiClient) {
      fetchGatewayInfo();
    } else {
      setGatewayInfo(null);
    }
  }, [selectedCustomerId, apiClient, fetchGatewayInfo]);

  // TODO: 这里是预留的新接口调用位置
  // 当前使用旧接口，后续替换为新接口
  const fetchBands = useCallback(async () => {
    if (!selectedCustomerId || !apiClient) {
      setBands([]);
      return;
    }
    setLoading(true);
    try {
      // 当前使用的旧接口 - 后续需要替换为新接口
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'CUSTOMER_BAND',
        colName: selectedCustomerId
      });
      
      if (resp.data?.code === 200) {
        let raw = resp.data.data;
        if (typeof raw === 'string') {
          raw = JSON.parse(raw);
        }
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list
          .filter(item => !item.colName || String(item.colName) === String(selectedCustomerId))
          .map(item => {
            // 解析会员绑定信息、展示设置和所属类型
            let boundMember = null;
            let showOnDashboard = false;
            let belong = 'venue'; // 默认为场馆
            if (item.remark) {
              try {
                const remarkData = JSON.parse(item.remark);
                // 只有当remark中有id字段时才认为绑定了会员
                if (remarkData.id) {
                  boundMember = {
                    id: remarkData.id,
                    babyName: remarkData.babyName,
                    photoAddress: remarkData.photoAddress
                  };
                }
                // 提取展示设置
                showOnDashboard = remarkData.showOnDashboard === true;
                // 提取所属类型，默认为venue
                belong = remarkData.belong === 'person' ? 'person' : 'venue';
              } catch (e) {
                console.warn('解析会员绑定信息失败:', e);
                boundMember = null;
              }
            }
            
            return {
              configId: item.configId,
              enumCode: item.enumCode,
              enumName: item.enumName,
              remark: item.remark || '',
              boundMember: boundMember, // 会员绑定信息对象（只有有id时才不为null）
              showOnDashboard: showOnDashboard,
              belong: belong, // 所属类型：venue(场馆)或person(个人)
              showSort: item.showSort ?? 0,
              state: item.state ?? 1
            };
          })
          .sort((a, b) => (a.showSort ?? 0) - (b.showSort ?? 0));
        setBands(normalized);
      } else {
        message.error(resp.data?.msg || '获取手环列表失败');
      }
    } catch (err) {
      console.error('获取手环列表异常:', err);
      message.error('获取手环列表失败');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerId, apiClient]);

  // 当selectedCustomerId变化时查询数据
  useEffect(() => {
    if (selectedCustomerId && apiClient) {
      fetchBands();
    } else if (!selectedCustomerId) {
      setBands([]);
    }
  }, [selectedCustomerId, apiClient, fetchBands]);

  const handleCustomerChange = (customerId) => {
    if (!customerId) {
      setSelectedCustomerId(undefined);
      setBands([]);
      return;
    }
    setSelectedCustomerId(customerId);
  };
  
  // 处理新增时的会员选择
  const handleMemberSelectForAdd = (member) => {
    setSelectedMemberForAdd(member);
    // 获取当前remark中的其他字段（如showOnDashboard）
    const currentRemark = addForm.getFieldValue('remark');
    let existingData = {};
    if (currentRemark) {
      try {
        existingData = JSON.parse(currentRemark);
      } catch (e) {
        // 如果解析失败，说明remark不是JSON格式，忽略
      }
    }
    // 合并会员信息到remark中
    addForm.setFieldsValue({
      remark: JSON.stringify({
        ...existingData,
        id: member.id,
        babyName: member.babyName,
        photoAddress: member.photoAddress
      })
    });
  };
  
  // 处理编辑时的会员选择
  const handleMemberSelectForEdit = (member) => {
    setSelectedMemberForEdit(member);
    // 获取当前remark中的其他字段（如showOnDashboard）
    const currentRemark = editForm.getFieldValue('remark');
    let existingData = {};
    if (currentRemark) {
      try {
        existingData = JSON.parse(currentRemark);
      } catch (e) {
        // 如果解析失败，说明remark不是JSON格式，忽略
      }
    }
    // 合并会员信息到remark中
    editForm.setFieldsValue({
      remark: JSON.stringify({
        ...existingData,
        id: member.id,
        babyName: member.babyName,
        photoAddress: member.photoAddress
      })
    });
  };

  const showModal = () => {
    setVisible(true);
    // 打开弹窗时自动加载数据
    if (selectedCustomerId && apiClient) {
      fetchBands();
    }
  };

  const handleCancel = () => {
    if (autoOpen && onBandsChange) {
      // autoOpen 模式下，通过 onBandsChange 通知父组件关闭
      onBandsChange();
    } else {
      setVisible(false);
    }
  };

  // 处理排序上调
  const handleMoveUp = async (record) => {
    const currentIndex = bands.findIndex(item => item.configId === record.configId);
    if (currentIndex <= 0) return;
    
    const prevRecord = bands[currentIndex - 1];
    
    try {
      // 交换两个记录的排序值
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: record.enumCode,
        enumName: record.enumName,
        remark: record.remark || '',
        showSort: prevRecord.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: record.configId
      });
      
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: prevRecord.enumCode,
        enumName: prevRecord.enumName,
        remark: prevRecord.remark || '',
        showSort: record.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: prevRecord.configId
      });
      
      message.success('排序调整成功');
      fetchBands();
    } catch (err) {
      console.error('排序调整异常:', err);
      message.error('排序调整失败');
    }
  };

  // 处理排序下调
  const handleMoveDown = async (record) => {
    const currentIndex = bands.findIndex(item => item.configId === record.configId);
    if (currentIndex >= bands.length - 1) return;
    
    const nextRecord = bands[currentIndex + 1];
    
    try {
      // 交换两个记录的排序值
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: record.enumCode,
        enumName: record.enumName,
        remark: record.remark || '',
        showSort: nextRecord.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: record.configId
      });
      
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: nextRecord.enumCode,
        enumName: nextRecord.enumName,
        remark: nextRecord.remark || '',
        showSort: record.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: nextRecord.configId
      });
      
      message.success('排序调整成功');
      fetchBands();
    } catch (err) {
      console.error('排序调整异常:', err);
      message.error('排序调整失败');
    }
  };

  // 计算下一个排序值
  const calcNextSort = () => {
    if (!bands.length) {
      return 10;
    }
    const maxSort = Math.max(...bands.map(item => item.showSort ?? 0));
    return maxSort + 10;
  };

  // 处理新增
  const handleAdd = () => {
    if (!selectedCustomerId) {
      message.warning('请先选择校区');
      return;
    }
    
    setAddModalVisible(true);
    addForm.resetFields();
    setSelectedMemberForAdd(null); // 清空会员选择
    addForm.setFieldsValue({ 
      showSort: calcNextSort(),
      showOnDashboard: true // 默认展示到大屏
    });
  };

  // 保存新增
  const handleSaveAdd = async () => {
    if (!selectedCustomerId || !apiClient) return;
    
    try {
      const values = await addForm.validateFields();
      
      // 检查编号是否重复
      const duplicateCode = bands.some(item => item.enumCode === values.enumCode);
      if (duplicateCode) {
        message.warning('手环编号已存在，请勿重复添加');
        return;
      }
      
      // 检查别名是否重复
      const duplicateName = bands.some(item => item.enumName === values.enumName);
      if (duplicateName) {
        message.warning('手环昵称已存在，请更换别名');
        return;
      }
      
      // 构建remark字段：合并会员信息和是否展示设置
      let remarkData = {};
      
      // 1. 先解析原有的remark数据
      if (values.remark) {
        try {
          remarkData = JSON.parse(values.remark);
        } catch (e) {
          // 如果解析失败，说明remark不是JSON格式
          remarkData = { _originalRemark: values.remark };
        }
      }
      
      // 2. 添加showOnDashboard字段
      remarkData.showOnDashboard = values.showOnDashboard !== false;
      
      const finalRemark = JSON.stringify(remarkData);
      
      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: values.enumCode,
        enumName: values.enumName,
        remark: finalRemark,
        showSort: values.showSort ?? calcNextSort(),
        state: 1,
        parentEnumCode: 0,
        configId: null
      };
      
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success('新增成功');
        setAddModalVisible(false);
        addForm.resetFields();
        fetchBands();
        // 通知父组件数据已变更
        if (onBandsChange) {
          onBandsChange();
        }
      } else {
        message.error(resp.data?.msg || '新增失败');
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      console.error('新增手环异常:', err);
      message.error('新增失败');
    }
  };

  // 取消新增
  const handleCancelAdd = () => {
    setAddModalVisible(false);
    addForm.resetFields();
    setSelectedMemberForAdd(null); // 清空会员选择
  };

  // 处理删除
  const handleDelete = async (record) => {
    if (!apiClient) return;
    try {
      // 如果是个人手环，先删除 PERSON_BAND 枚举数据（个人与手环的捆绑信息）
      if (record.belong === 'person' && record.boundMember?.id) {
        try {
          // 查询 PERSON_BAND 表中该会员的绑定记录
          const personBandResp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
            tableName: 'PERSON_BAND',
            colName: String(record.boundMember.id)
          });
          
          if (personBandResp.data?.code === 200) {
            let rawData = personBandResp.data.data;
            if (typeof rawData === 'string') {
              rawData = JSON.parse(rawData);
            }
            const personBandList = Array.isArray(rawData) ? rawData : [];
            
            // 找到匹配的手环编号记录并删除
            for (const item of personBandList) {
              if (item.enumCode === record.enumCode) {
                await apiClient.delete(`/portal-server-prod/sys/enumconfig/v1/delete/${item.configId}`);
                console.log(`已删除 PERSON_BAND 记录: configId=${item.configId}, babyId=${record.boundMember.id}`);
                break;
              }
            }
          }
        } catch (personBandErr) {
          console.error('删除 PERSON_BAND 记录失败:', personBandErr);
          // 继续删除手环本身，不阻断主流程
        }
      }
      
      const resp = await apiClient.delete(`/portal-server-prod/sys/enumconfig/v1/delete/${record.configId}`);
      if (resp.data?.code === 200) {
        message.success('删除成功');
        fetchBands();
        // 通知父组件数据已变更
        if (onBandsChange) {
          onBandsChange();
        }
      } else {
        message.error(resp.data?.msg || '删除失败');
      }
    } catch (err) {
      console.error('删除手环异常:', err);
      message.error('删除失败');
    }
  };

  // 处理编辑
  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditModalVisible(true);
    
    // 解析remark中的showOnDashboard字段
    let showOnDashboard = true; // 默认为true
    if (record.remark) {
      try {
        const remarkData = JSON.parse(record.remark);
        // 如果showOnDashboard未定义，默认为true
        showOnDashboard = remarkData.showOnDashboard !== false;
      } catch (e) {
        // 解析失败，使用默认值
      }
    }
    
    // 初始化表单值
    editForm.setFieldsValue({
      enumCode: record.enumCode,
      enumName: record.enumName,
      remark: record.remark || '',
      showOnDashboard: showOnDashboard
    });
    
    // 初始化会员选择状态
    setSelectedMemberForEdit(record.boundMember);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!editingRecord || !apiClient || !selectedCustomerId) return;
    
    try {
      const values = await editForm.validateFields();
      
      // 检查编号是否与其他记录重复
      const duplicateCode = bands.some(
        item => item.enumCode === values.enumCode && item.configId !== editingRecord.configId
      );
      if (duplicateCode) {
        message.warning('手环编号已存在，请勿重复添加');
        return;
      }
      
      // 检查别名是否与其他记录重复
      const duplicateName = bands.some(
        item => item.enumName === values.enumName && item.configId !== editingRecord.configId
      );
      if (duplicateName) {
        message.warning('手环昵称已存在，请更换别名');
        return;
      }
      
      // 构建remark字段：合并会员信息和是否展示设置
      let remarkData = {};
      
      // 1. 先解析原有的remark数据
      if (values.remark) {
        try {
          remarkData = JSON.parse(values.remark);
        } catch (e) {
          // 如果解析失败，说明remark不是JSON格式，作为字符串保留
          remarkData = { _originalRemark: values.remark };
        }
      }
      
      // 2. 添加/更新showOnDashboard字段
      remarkData.showOnDashboard = values.showOnDashboard !== false;
      
      // 3. 如果没有会员信息但有原始remark，保留它
      const finalRemark = JSON.stringify(remarkData);
      
      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: values.enumCode,
        enumName: values.enumName,
        remark: finalRemark,
        showSort: editingRecord.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: editingRecord.configId
      };
      
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success('修改成功');
        setEditModalVisible(false);
        setEditingRecord(null);
        editForm.resetFields();
        fetchBands();
        // 通知父组件数据已变更
        if (onBandsChange) {
          onBandsChange();
        }
      } else {
        message.error(resp.data?.msg || '保存失败');
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      console.error('保存手环异常:', err);
      message.error('保存失败');
    }
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    editForm.resetFields();
    setSelectedMemberForEdit(null); // 清空会员选择
  };

  // 处理取消绑定
  const handleUnbind = async (record) => {
    if (!apiClient || !selectedCustomerId) return;

    // 如果是个人专属手环，需要二次确认
    if (record.belong === 'person') {
      Modal.confirm({
        title: '确认解除个人绑定',
        content: '当前绑定的是个人专属手环，真的要解除绑定吗？',
        okText: '确认解除',
        cancelText: '取消',
        onOk: () => doUnbind(record)
      });
    } else {
      // 场馆手环直接解除
      doUnbind(record);
    }
  };

  // 执行取消绑定
  const doUnbind = async (record) => {
    try {
      // 解析当前remark，保留showOnDashboard和belong设置
      let remarkData = {};
      if (record.remark) {
        try {
          remarkData = JSON.parse(record.remark);
        } catch (e) {
          // 解析失败则忽略
        }
      }
      
      // 只保留showOnDashboard和belong字段，移除会员绑定信息
      const newRemark = JSON.stringify({
        showOnDashboard: remarkData.showOnDashboard === true,
        belong: remarkData.belong === 'person' ? 'person' : 'venue'
      });

      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: record.enumCode,
        enumName: record.enumName,
        remark: newRemark, // 只保留showOnDashboard和belong，清空会员绑定信息
        showSort: record.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: record.configId
      };

      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success('取消绑定成功');
        fetchBands();
        // 通知父组件数据已变更
        if (onBandsChange) {
          onBandsChange();
        }
      } else {
        message.error(resp.data?.msg || '取消绑定失败');
      }
    } catch (err) {
      console.error('取消绑定异常:', err);
      message.error('取消绑定失败');
    }
  };

  // 处理快捷更新是否展示
  const handleToggleShowOnDashboard = async (record, showOnDashboard) => {
    if (!apiClient || !selectedCustomerId) return;

    try {
      // 解析当前remark
      let remarkData = {};
      if (record.remark) {
        try {
          remarkData = JSON.parse(record.remark);
        } catch (e) {
          remarkData = { _originalRemark: record.remark };
        }
      }

      // 设置showOnDashboard
      remarkData.showOnDashboard = showOnDashboard;

      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: record.enumCode,
        enumName: record.enumName,
        remark: JSON.stringify(remarkData),
        showSort: record.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: record.configId
      };

      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success(showOnDashboard ? '已设置为展示' : '已设置为隐藏');
        fetchBands();
        // 通知父组件数据已变更
        if (onBandsChange) {
          onBandsChange();
        }
      } else {
        message.error(resp.data?.msg || '更新失败');
      }
    } catch (err) {
      console.error('快捷更新展示状态异常:', err);
      message.error('更新失败');
    }
  };

  // 处理所属类型变更
  const handleBelongChange = async (record, belong) => {
    if (!apiClient || !selectedCustomerId) return;

    try {
      // 解析当前remark
      let remarkData = {};
      if (record.remark) {
        try {
          remarkData = JSON.parse(record.remark);
        } catch (e) {
          remarkData = { _originalRemark: record.remark };
        }
      }

      // 设置belong
      remarkData.belong = belong;

      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: record.enumCode,
        enumName: record.enumName,
        remark: JSON.stringify(remarkData),
        showSort: record.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: record.configId
      };

      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success(belong === 'person' ? '已设置为个人' : '已设置为场馆');
        fetchBands();
        // 通知父组件数据已变更
        if (onBandsChange) {
          onBandsChange();
        }
      } else {
        message.error(resp.data?.msg || '更新失败');
      }
    } catch (err) {
      console.error('更新所属类型异常:', err);
      message.error('更新失败');
    }
  };

  // ==================== 网关管理相关函数 ====================
  
  // 计算网关下一个排序值
  const calcGatewayNextSort = () => {
    if (!gatewayList.length) {
      return 10;
    }
    const maxSort = Math.max(...gatewayList.map(item => item.showSort ?? 0));
    return maxSort + 10;
  };

  // 处理网关排序上调
  const handleGatewayMoveUp = async (record) => {
    const currentIndex = gatewayList.findIndex(item => item.configId === record.configId);
    if (currentIndex <= 0) return;
    
    const prevRecord = gatewayList[currentIndex - 1];
    
    try {
      // 交换两个记录的排序值
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_GATEWAY',
        colName: String(selectedCustomerId),
        enumCode: record.enumCode,
        enumName: record.enumName,
        remark: record.remark || '',
        showSort: prevRecord.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: record.configId
      });
      
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_GATEWAY',
        colName: String(selectedCustomerId),
        enumCode: prevRecord.enumCode,
        enumName: prevRecord.enumName,
        remark: prevRecord.remark || '',
        showSort: record.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: prevRecord.configId
      });
      
      message.success('排序调整成功');
      fetchGatewayInfo();
    } catch (err) {
      console.error('网关排序调整异常:', err);
      message.error('排序调整失败');
    }
  };

  // 处理网关排序下调
  const handleGatewayMoveDown = async (record) => {
    const currentIndex = gatewayList.findIndex(item => item.configId === record.configId);
    if (currentIndex >= gatewayList.length - 1) return;
    
    const nextRecord = gatewayList[currentIndex + 1];
    
    try {
      // 交换两个记录的排序值
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_GATEWAY',
        colName: String(selectedCustomerId),
        enumCode: record.enumCode,
        enumName: record.enumName,
        remark: record.remark || '',
        showSort: nextRecord.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: record.configId
      });
      
      await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', {
        tableName: 'CUSTOMER_GATEWAY',
        colName: String(selectedCustomerId),
        enumCode: nextRecord.enumCode,
        enumName: nextRecord.enumName,
        remark: nextRecord.remark || '',
        showSort: record.showSort,
        state: 1,
        parentEnumCode: 0,
        configId: nextRecord.configId
      });
      
      message.success('排序调整成功');
      fetchGatewayInfo();
    } catch (err) {
      console.error('网关排序调整异常:', err);
      message.error('排序调整失败');
    }
  };

  // 处理新增网关
  const handleGatewayAdd = () => {
    if (!selectedCustomerId) {
      message.warning('请先选择校区');
      return;
    }
    
    setGatewayAddModalVisible(true);
    gatewayAddForm.resetFields();
    gatewayAddForm.setFieldsValue({ showSort: calcGatewayNextSort() });
  };

  // 保存新增网关
  const handleSaveGatewayAdd = async () => {
    if (!selectedCustomerId || !apiClient) return;
    
    try {
      const values = await gatewayAddForm.validateFields();
      
      // 检查网关编码是否重复
      const duplicateCode = gatewayList.some(item => item.enumCode === values.enumCode);
      if (duplicateCode) {
        message.warning('网关编码已存在，请勿重复添加');
        return;
      }
      
      const payload = {
        tableName: 'CUSTOMER_GATEWAY',
        colName: String(selectedCustomerId),
        enumCode: values.enumCode,
        enumName: values.enumName || values.enumCode,
        remark: values.remark || '',
        showSort: values.showSort ?? calcGatewayNextSort(),
        state: 1,
        parentEnumCode: 0,
        configId: null
      };
      
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success('新增网关成功');
        setGatewayAddModalVisible(false);
        gatewayAddForm.resetFields();
        fetchGatewayInfo();
      } else {
        message.error(resp.data?.msg || '新增网关失败');
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      console.error('新增网关异常:', err);
      message.error('新增网关失败');
    }
  };

  // 取消新增网关
  const handleCancelGatewayAdd = () => {
    setGatewayAddModalVisible(false);
    gatewayAddForm.resetFields();
  };

  // 处理编辑网关
  const handleGatewayEdit = (record) => {
    setEditingGateway(record);
    setGatewayEditModalVisible(true);
    
    // 初始化表单值
    gatewayEditForm.setFieldsValue({
      enumCode: record.enumCode,
      enumName: record.enumName,
      remark: record.remark || ''
    });
  };

  // 保存编辑网关
  const handleSaveGatewayEdit = async () => {
    if (!editingGateway || !apiClient || !selectedCustomerId) return;
    
    try {
      const values = await gatewayEditForm.validateFields();
      
      // 检查编码是否与其他记录重复
      const duplicateCode = gatewayList.some(
        item => item.enumCode === values.enumCode && item.configId !== editingGateway.configId
      );
      if (duplicateCode) {
        message.warning('网关编码已存在，请勿重复添加');
        return;
      }
      
      const payload = {
        tableName: 'CUSTOMER_GATEWAY',
        colName: String(selectedCustomerId),
        enumCode: values.enumCode,
        enumName: values.enumName || values.enumCode,
        remark: values.remark || '',
        showSort: editingGateway.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: editingGateway.configId
      };
      
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success('修改网关成功');
        setGatewayEditModalVisible(false);
        setEditingGateway(null);
        gatewayEditForm.resetFields();
        fetchGatewayInfo();
      } else {
        message.error(resp.data?.msg || '保存网关失败');
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      console.error('保存网关异常:', err);
      message.error('保存网关失败');
    }
  };

  // 取消编辑网关
  const handleCancelGatewayEdit = () => {
    setGatewayEditModalVisible(false);
    setEditingGateway(null);
    gatewayEditForm.resetFields();
  };

  // 处理删除网关
  const handleGatewayDelete = async (record) => {
    if (!apiClient) return;
    try {
      const resp = await apiClient.delete(`/portal-server-prod/sys/enumconfig/v1/delete/${record.configId}`);
      if (resp.data?.code === 200) {
        message.success('删除网关成功');
        fetchGatewayInfo();
      } else {
        message.error(resp.data?.msg || '删除网关失败');
      }
    } catch (err) {
      console.error('删除网关异常:', err);
      message.error('删除网关失败');
    }
  };

  // 关闭网关管理弹窗
  const handleCloseGatewayModal = () => {
    setGatewayModalVisible(false);
  };

  // 网关表格列定义
  const gatewayColumns = [
    {
      title: '网关编码',
      dataIndex: 'enumCode',
      key: 'enumCode',
      width: 200,
    },
    {
      title: '网关名称',
      dataIndex: 'enumName',
      key: 'enumName',
      width: 200,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      render: (remark) => remark || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => {
        const currentIndex = gatewayList.findIndex(item => item.configId === record.configId);
        
        return (
          <Space size="small">
            <Button 
              size="small" 
              icon={<ArrowUpOutlined />} 
              onClick={() => handleGatewayMoveUp(record)}
              disabled={currentIndex === 0}
            />
            <Button 
              size="small" 
              icon={<ArrowDownOutlined />} 
              onClick={() => handleGatewayMoveDown(record)}
              disabled={currentIndex === gatewayList.length - 1}
            />
            <Button 
              size="small" 
              icon={<EditOutlined />} 
              onClick={() => handleGatewayEdit(record)}
            />
            <Popconfirm 
              title="确定删除该网关？" 
              onConfirm={() => handleGatewayDelete(record)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    }
  ];

  const columns = [
    {
      title: '手环编号',
      dataIndex: 'enumCode',
      key: 'enumCode',
      width: 130,
    },
    {
      title: '手环昵称',
      dataIndex: 'enumName',
      key: 'enumName',
      width: 100,
    },
    {
      title: '绑定会员',
      dataIndex: 'boundMember',
      key: 'boundMember',
      width: 180,
      render: (boundMember, record) => {
        if (!boundMember) {
          return <span style={{ color: '#999' }}>未绑定</span>;
        }
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={boundMember.photoAddress} 
                alt={boundMember.babyName}
                style={{ 
                  width: 28, 
                  height: 28, 
                  borderRadius: '50%', 
                  marginRight: 6,
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvcnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPk1lbWJlcjwvdGV4dD48L3N2Zz4=';
                }}
              />
              <span style={{ fontSize: '13px' }}>{boundMember.babyName}</span>
            </div>
            <Button 
              type="link" 
              danger 
              size="small" 
              style={{ padding: '0 4px', fontSize: '12px' }}
              onClick={() => handleUnbind(record)}
            >
              {record.belong === 'person' ? '解除个人' : '解绑'}
            </Button>
          </div>
        );
      }
    },
    {
      title: '所属',
      dataIndex: 'belong',
      key: 'belong',
      width: 85,
      render: (belong) => {
        return (
          <Tag color={belong === 'person' ? 'blue' : 'green'}>
            {belong === 'person' ? '个人' : '场馆'}
          </Tag>
        );
      }
    },
    {
      title: '大屏展示',
      dataIndex: 'showOnDashboard',
      key: 'showOnDashboard',
      width: 95,
      render: (_, record) => {
        // 解析showOnDashboard值
        let showOnDashboard = false;
        if (record.remark) {
          try {
            const remarkData = JSON.parse(record.remark);
            showOnDashboard = remarkData.showOnDashboard === true;
          } catch (e) {
            showOnDashboard = false;
          }
        }
        
        return (
          <Button
            type={showOnDashboard ? 'primary' : 'default'}
            size="small"
            style={{ fontSize: '12px', padding: '0 8px' }}
            onClick={() => handleToggleShowOnDashboard(record, !showOnDashboard)}
          >
            {showOnDashboard ? '展示' : '隐藏'}
          </Button>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        const currentIndex = bands.findIndex(item => item.configId === record.configId);
        
        return (
          <Space size="small">
            <Button 
              size="small" 
              icon={<ArrowUpOutlined />} 
              onClick={() => handleMoveUp(record)}
              disabled={currentIndex === 0}
            />
            <Button 
              size="small" 
              icon={<ArrowDownOutlined />} 
              onClick={() => handleMoveDown(record)}
              disabled={currentIndex === bands.length - 1}
            />
            <Button 
              size="small" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
            <Popconfirm 
              title="确定删除该手环？" 
              onConfirm={() => handleDelete(record)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    }
  ];

  // 主要内容渲染
  const renderContent = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <CustomerSelector
            value={selectedCustomerId}
            onChange={handleCustomerChange}
            placeholder="选择校区查看手环"
            allowClear={false}
            showTeamSelector={false}
            style={{ width: 300 }}
          />
          {gatewayInfo && (
            <div 
              onClick={() => setGatewayModalVisible(true)}
              style={{ 
                padding: '4px 12px', 
                backgroundColor: '#f6ffed', 
                border: '1px solid #b7eb8f',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#389e0d',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d9f7be';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f6ffed';
              }}
            >
              <span style={{ fontWeight: 'bold' }}>网关：</span>
              <span>{gatewayInfo}</span>
            </div>
          )}
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
          disabled={!selectedCustomerId}
        >
          新增手环
        </Button>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : !selectedCustomerId ? (
        <Empty description="请先选择校区" />
      ) : bands.length === 0 ? (
        <Empty description="暂无手环数据" />
      ) : (
        <>
          <Table
            dataSource={bands}
            columns={columns}
            rowKey="configId"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
            scroll={{ y: 300, x: 700 }}
          />
        </>
      )}
    </Space>
  );

  // 底部渲染
  const renderFooter = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      {/* 心率告警阈值配置 - 与关闭按钮同一水平线，最左侧 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontWeight: 'bold', color: '#333' }}>心率告警配置：</span>
        <Select
          value={heartRateThreshold.toString()}
          onChange={(value) => {
            const threshold = parseInt(value);
            if (!isNaN(threshold) && threshold >= 100 && threshold <= 300) {
              setHeartRateThreshold(threshold);
              localStorage.setItem('venueDashboardHeartRateThreshold', threshold.toString());
            }
          }}
          style={{ width: '120px' }}
          options={Array.from({ length: 41 }, (_, i) => {
            const value = 100 + i * 5;
            return { value: value.toString(), label: `${value} bpm` };
          })}
        />
        <span style={{ color: '#666', fontSize: '12px' }}>默认：200 bpm</span>
      </div>
      
      {/* 关闭按钮 - 最右侧 */}
      <Button key="close" onClick={handleCancel}>
        关闭
      </Button>
    </div>
  );

  return (
    <>
      {!autoOpen && (
        <Button 
          icon={<EyeOutlined />} 
          onClick={showModal}
          {...triggerButtonProps}
        >
          {triggerButtonProps?.children || '查看手环'}
        </Button>
      )}
      
      {/* autoOpen 为 true 时直接渲染内容，不使用 Modal */}
      {autoOpen ? (
        <>
          {renderContent()}
          <div style={{ marginTop: 16 }}>
            {renderFooter()}
          </div>
        </>
      ) : (
        <Modal
          title="运动手环列表"
          open={visible}
          onCancel={handleCancel}
          footer={[renderFooter()]}
          width={1000}
          {...modalProps}
        >
          {renderContent()}
        </Modal>
      )}
      
      {/* 新增手环弹窗 */}
      <Modal
        title="新增手环"
        open={addModalVisible}
        onOk={handleSaveAdd}
        onCancel={handleCancelAdd}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            label="手环编号"
            name="enumCode"
            rules={[{ required: true, message: '请输入手环编号' }]}
          >
            <Input placeholder="请输入手环编号" />
          </Form.Item>
          <Form.Item
            label="手环昵称"
            name="enumName"
            rules={[{ required: true, message: '请输入手环昵称' }]}
          >
            <BandNickNameSelector
              options={bandNickNameOptions}
              form={addForm}
              fieldName="enumName"
            />
          </Form.Item>
          <Form.Item 
            label="绑定会员" 
            name="remark"
            rules={[{ required: false }]}
          >
            <div>
              {selectedMemberForAdd ? (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <img 
                    src={selectedMemberForAdd.photoAddress} 
                    alt={selectedMemberForAdd.babyName}
                    style={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      marginRight: 12,
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvcnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPk1lbWJlcjwvdGV4dD48L3N2Zz4=';
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{selectedMemberForAdd.babyName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>ID: {selectedMemberForAdd.id}</div>
                  </div>
                  <Button 
                    type="link" 
                    danger 
                    size="small" 
                    onClick={() => {
                      setSelectedMemberForAdd(null);
                      addForm.setFieldsValue({ remark: '' });
                    }}
                    style={{ marginLeft: 'auto' }}
                  >
                    取消绑定
                  </Button>
                </div>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ color: '#999', fontSize: '12px' }}>请选择要绑定的会员：</span>
                </div>
              )}
              <MemberSelector 
                onSelect={handleMemberSelectForAdd}
                selectedMemberIds={selectedMemberForAdd ? [selectedMemberForAdd.id] : []}
              />
            </div>
          </Form.Item>
          <Form.Item
            label="是否展示到大屏"
            name="showOnDashboard"
            rules={[{ required: false }]}
          >
            <Radio.Group>
              <Radio value={true}>是</Radio>
              <Radio value={false}>否</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 编辑手环弹窗 */}
      <Modal
        title="编辑手环"
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={handleCancelEdit}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="手环编号"
            name="enumCode"
            rules={[{ required: true, message: '请输入手环编号' }]}
          >
            <Input placeholder="请输入手环编号" />
          </Form.Item>
          <Form.Item
            label="手环昵称"
            name="enumName"
            rules={[{ required: true, message: '请输入手环昵称' }]}
          >
            <BandNickNameSelector
              options={bandNickNameOptions}
              form={editForm}
              fieldName="enumName"
            />
          </Form.Item>
          <Form.Item 
            label="绑定会员" 
            name="remark"
            rules={[{ required: false }]}
          >
            <div>
              {selectedMemberForEdit ? (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <img 
                    src={selectedMemberForEdit.photoAddress} 
                    alt={selectedMemberForEdit.babyName}
                    style={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      marginRight: 12,
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvcnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPk1lbWJlcjwvdGV4dD48L3N2Zz4=';
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{selectedMemberForEdit.babyName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>ID: {selectedMemberForEdit.id}</div>
                  </div>
                  <Button 
                    type="link" 
                    danger 
                    size="small" 
                    onClick={() => {
                      setSelectedMemberForEdit(null);
                      editForm.setFieldsValue({ remark: '' });
                    }}
                    style={{ marginLeft: 'auto' }}
                  >
                    取消绑定
                  </Button>
                </div>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ color: '#999', fontSize: '12px' }}>请选择要绑定的会员：</span>
                </div>
              )}
              <MemberSelector 
                onSelect={handleMemberSelectForEdit}
                selectedMemberIds={selectedMemberForEdit ? [selectedMemberForEdit.id] : []}
              />
            </div>
          </Form.Item>
          <Form.Item
            label="是否展示到大屏"
            name="showOnDashboard"
            rules={[{ required: false }]}
          >
            <Radio.Group>
              <Radio value={true}>是</Radio>
              <Radio value={false}>否</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 网关管理弹窗 */}
      <Modal
        title="网关管理"
        open={gatewayModalVisible}
        onCancel={handleCloseGatewayModal}
        footer={[
          <Button key="close" onClick={handleCloseGatewayModal}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleGatewayAdd}
              disabled={!selectedCustomerId}
            >
              新增网关
            </Button>
          </div>
          
          {!selectedCustomerId ? (
            <Empty description="请先选择校区" />
          ) : gatewayList.length === 0 ? (
            <Empty description="暂无网关数据" />
          ) : (
            <Table
              dataSource={gatewayList}
              columns={gatewayColumns}
              rowKey="configId"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`
              }}
              scroll={{ y: 300, x: 600 }}
            />
          )}
        </Space>
      </Modal>
      
      {/* 新增网关弹窗 */}
      <Modal
        title="新增网关"
        open={gatewayAddModalVisible}
        onOk={handleSaveGatewayAdd}
        onCancel={handleCancelGatewayAdd}
        destroyOnClose
      >
        <Form form={gatewayAddForm} layout="vertical">
          <Form.Item
            label="网关编码"
            name="enumCode"
            rules={[{ required: true, message: '请输入网关编码' }]}
          >
            <Input placeholder="请输入网关编码" />
          </Form.Item>
          <Form.Item
            label="网关名称"
            name="enumName"
            rules={[{ required: false, message: '请输入网关名称' }]}
          >
            <Input placeholder="请输入网关名称（可选，默认为编码）" />
          </Form.Item>
          <Form.Item
            label="备注"
            name="remark"
            rules={[{ required: false }]}
          >
            <Input.TextArea placeholder="请输入备注信息（可选）" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 编辑网关弹窗 */}
      <Modal
        title="编辑网关"
        open={gatewayEditModalVisible}
        onOk={handleSaveGatewayEdit}
        onCancel={handleCancelGatewayEdit}
        destroyOnClose
      >
        <Form form={gatewayEditForm} layout="vertical">
          <Form.Item
            label="网关编码"
            name="enumCode"
            rules={[{ required: true, message: '请输入网关编码' }]}
          >
            <Input placeholder="请输入网关编码" />
          </Form.Item>
          <Form.Item
            label="网关名称"
            name="enumName"
            rules={[{ required: false, message: '请输入网关名称' }]}
          >
            <Input placeholder="请输入网关名称" />
          </Form.Item>
          <Form.Item
            label="备注"
            name="remark"
            rules={[{ required: false }]}
          >
            <Input.TextArea placeholder="请输入备注信息" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerBandsViewer;